<template>
  <ApplicationCard
    :type="reasonTypeTitle"
    :to="getPath"
    :status="application.landlordStatus"
    :subtitle="subtitle(application.landlordStatus)"
    :subtitle-class="subtitleClass(application.landlordStatus)"
    :is-new="isNew"
    :full-name="fullName"
    :last-name="lastName"
    :status-date="statusDate"
    :unread-notes="application.unreadNotes"
    :closed-statuses="closedStatuses"
    :application-id="`${application.id}`"
    :application-tags="application.tags"
    :show-tag-button="showTagButton"
    :is-overtime="daysLeftOvertime"
    :landlord-name="account?.managerId ? application.landlordName : ''"
    :chain-info="
      showChainInfo && ahAppsChains[application.id]
        ? {
            text: application.latestVersion ? 'Förlängning' : '',
            id: ahAppsChains[application.id],
            reverse: application.latestVersion === 0
          }
        : null
    "
    :enable-link="enableLink"
  >
    <template #body>
      <div class="application-card-info-container">
        <div class="application-card-info">
          <p>
            <span class="capitalize-first">{{ currentTenantsNameGroup }}</span>
            <span class="light">{{ application.apartment?.address }}</span>
          </p>
          <p v-if="application.secondaryTenants[0]">
            <span class="capitalize-first">{{
              application.secondaryTenants[0].name
            }}</span>
            <span class="light">{{
              application.secondaryTenants[0].address
            }}</span>
          </p>
          <p v-if="application.secondaryTenants[1]">
            <span class="capitalize-first">{{
              application.secondaryTenants[1].name
            }}</span>
            <span class="light">{{
              application.secondaryTenants[1].address
            }}</span>
          </p>
          <p>Hyra: {{ application.apartment?.rent }} kr</p>
          <p>Lägenhetsnr: {{ application.apartment?.apartmentNumber }}</p>
          <p>
            Objektnr:
            {{ application.apartment?.objectNumber }}
          </p>
        </div>
      </div>
    </template>
    <template #footer>
      <template
        v-if="
          validDate(application.reason.rentFrom) &&
          validDate(application.reason.rentTo)
        "
        ><p v-if="['APPROVED', 'CLOSED'].includes(application.landlordStatus)">
          Uthyrningsperiod:
          <br />
          <b
            >{{ new Date(application?.startDate).toLocaleDateString('sv-SE') }}
          </b>
          -
          <b>{{
            new Date(application?.endDate).toLocaleDateString('sv-SE')
          }}</b>
          <template v-if="application.landlordStatus === 'APPROVED'">
            <br />
            <i
              v-if="showDaysLeft"
              class="days-left"
              :class="{
                overtime: daysLeftOvertime
              }"
              >(<strong>{{ daysLeftStr }}</strong
              >{{ daysLeftMsg }})</i
            >
          </template>
        </p>
        <p v-else>
          Önskar hyra ut:
          <br />
          <b
            >{{
              new Date(application?.reason.rentFrom).toLocaleDateString('sv-SE')
            }}
          </b>
          -
          <b>
            {{
              new Date(application?.reason.rentTo).toLocaleDateString('sv-SE')
            }}</b
          >
        </p></template
      >
    </template>
    <template #overlay>
      <div v-if="daysLeftOvertime" class="close-case-overlay">
        <BaseButton
          :is-warning="true"
          :is-rounded="true"
          class="close-case-btn"
          @click.stop.prevent="openCloseConfirm"
        >
          Stäng ärende
        </BaseButton>
      </div>
      <Teleport to="body">
        <SubletCloseModal
          v-if="closeConfirmVisible"
          :application-id="application.id"
          @close="closeConfirmVisible = false"
          @cancelled="closeConfirmVisible = false"
        />
      </Teleport>
    </template>
  </ApplicationCard>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import statusTypes from '@/utils/statusTypes';

import ApplicationCard from '@/components/ApplicationCard.vue';
import BaseButton from '@/components/BaseButton.vue';
import SubletCloseModal from '@/components/SubletCloseModal.vue';
import validDate from '@/utils/validDate';

export default {
  name: 'SubletApplicationCard',
  components: {
    ApplicationCard,
    BaseButton,
    SubletCloseModal
  },
  props: {
    application: {
      type: Object,
      required: true
    },
    isNew: {
      type: Boolean,
      default: false
    },
    fullName: {
      type: String,
      default: ''
    },
    lastName: {
      type: String,
      default: ''
    },
    statusDate: {
      type: String,
      default: ''
    },
    closedStatuses: {
      type: Array,
      default: () => []
    },
    reasonTypeTitle: {
      type: String,
      required: true
    },
    toPath: {
      type: String,
      default: ''
    },
    showTagButton: {
      type: Boolean,
      default: true
    },
    showDaysLeft: {
      type: Boolean,
      default: true
    },
    enableLink: {
      type: Boolean,
      default: true
    },
    showChainInfo: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      validDate,
      closeConfirmVisible: false
    };
  },
  computed: {
    ...mapGetters({
      ahAppsChains: 'applications/ahAppsChains'
    }),
    ...mapState({
      account: state => state.app.account
    }),
    getPath() {
      return `${this.toPath}/${this.application.id}`;
    },
    currentTenantsNameGroup() {
      const tenant1Name = this.application.currentTenants[0]?.name;
      const tenant2Name = this.application.currentTenants[1]?.name;

      if (tenant1Name && tenant2Name) {
        return `${tenant1Name} & ${tenant2Name}`;
      } else if (tenant1Name) {
        return tenant1Name;
      }
      return '';
    },
    daysLeftOvertime() {
      if (this.application.landlordStatus !== 'APPROVED') {
        return false;
      }
      return 0 >= this.daysLeft;
    },
    daysLeft() {
      const currentDate = new Date();
      const endDate = new Date(this.application.endDate);

      const timeDifference = endDate - currentDate;

      return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    },
    daysLeftMsg() {
      if (this.daysLeftOvertime) {
        return ' dagar efter avtalad period';
      }
      return this.daysLeft === 1 ? ' dag kvar' : ' dagar kvar';
    },
    daysLeftStr() {
      const posDaysLeft = Math.abs(this.daysLeft);
      switch (posDaysLeft) {
        case 1:
          return 'en';
        case 2:
          return 'två';
        case 3:
          return 'tre';
        case 4:
          return 'fyra';
        case 5:
          return 'fem';
        case 6:
          return 'sex';
        case 7:
          return 'sju';
        case 8:
          return 'åtta';
        case 9:
          return 'nio';
        default:
          return posDaysLeft;
      }
    }
  },
  methods: {
    openCloseConfirm() {
      this.closeConfirmVisible = true;
    },
    subtitle(landlordStatus) {
      let text = '';
      switch (landlordStatus) {
        case statusTypes.CLOSED:
          text = 'Avslutad';
          break;
        case statusTypes.APPROVED:
          text = 'Godkänd';
          break;
        case statusTypes.REJECTED:
          text = 'Nekad';
          break;
        case statusTypes.ABORTED:
          text = 'Avbruten';
          break;
        default:
          text = '';
      }
      return text;
    },
    subtitleClass(landlordStatus) {
      switch (landlordStatus) {
        case statusTypes.CLOSED:
          return 'closed-title';
        case statusTypes.APPROVED:
          return 'closedApproved-title';
        case statusTypes.REJECTED:
          return 'rejected-title';
        case statusTypes.ABORTED:
          return 'aborted-title';
        default:
          return '';
      }
    }
  }
};
</script>

<style scoped>
.application-card-info-container {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.2rem;
  color: var(--color-dark-blue);
  font-size: clamp(1.2rem, 0.7vw, 1.4rem);
  font-weight: 600;
}
.application-card-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.light {
  color: #878b9d;
  font-size: clamp(1rem, 0.6vw, 1.2rem);
  font-weight: 600;
  margin-left: 0.2rem;
}

.days-left.overtime {
  color: var(--color-red);
}

.days-left strong {
  font-weight: 800;
}

/* Overlay som täcker kortet med "Stäng ärende"-knapp för utlöpta ärenden */
.close-case-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  border-radius: 10px;
  pointer-events: none;
}
.close-case-btn {
  pointer-events: auto;
}
</style>
